import React from "react";
import Select from "react-select";
import Http from "../../../Common/Http";

export const NAVY = "#2a2962";
export const RED = "#e42320";
export const GREEN = "#1e7e34";

export const TYPE_LABELS = {
  journal: "Journal",
  payment: "Payment",
  receipt: "Receipt",
  contra: "Contra",
};

// Each type runs its own numbering sequence under this prefix.
export const TYPE_OPTIONS = [
  { value: "journal", label: "Journal (JV)" },
  { value: "payment", label: "Payment (PV)" },
  { value: "receipt", label: "Receipt (RV)" },
  { value: "contra", label: "Contra (CV)" },
];

export const money = (value) =>
  (parseFloat(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Local calendar date as YYYY-MM-DD. toISOString() would convert to UTC first,
// which in Dhaka (UTC+6) turns local midnight into the previous day.
export const toInput = (date) => date.toLocaleDateString("en-CA");

// Amounts are compared in whole paisa, so 0.1 + 0.2 still balances 0.3.
const paisa = (value) => Math.round((parseFloat(value) || 0) * 100);

export const lineTotals = (list) => {
  let debit = 0;
  let credit = 0;
  (list || []).forEach((line) => {
    if (parseFloat(line.debit) > 0) debit += paisa(line.debit);
    if (parseFloat(line.credit) > 0) credit += paisa(line.credit);
  });
  return { debit: debit / 100, credit: credit / 100 };
};

/**
 * Checks the lines that carry an account and returns what to post, or the
 * first reason not to. Computed from the list itself, so callers can act on
 * the result straight away instead of waiting on error state to update.
 */
export const validateLines = (list) => {
  const items = [];
  let debit = 0;
  let credit = 0;

  for (let index = 0; index < (list || []).length; index++) {
    const line = list[index];
    if (!(line.chart_of_account_id > 0)) continue;

    const lineDebit = parseFloat(line.debit) > 0 ? paisa(line.debit) : 0;
    const lineCredit = parseFloat(line.credit) > 0 ? paisa(line.credit) : 0;

    if (!lineDebit && !lineCredit) {
      return {
        message: `Line ${index + 1} has an account but no debit or credit amount.`,
      };
    }

    debit += lineDebit;
    credit += lineCredit;
    items.push(line);
  }

  if (!debit || !credit) {
    return {
      message:
        "You must fill out at least two detail lines: one debit and one credit.",
    };
  }
  if (debit !== credit) {
    return {
      message: `Amount Not Match. Debits and credits differ by ${money(
        Math.abs(debit - credit) / 100,
      )}.`,
    };
  }

  return { message: null, items, total: debit / 100 };
};

// References on an account that still have an amount outstanding. The edit
// form passes its own voucher so that voucher's previous lines do not count.
export const fetchOpenReferences = (accountId, excludeVoucherId) =>
  Http.get("/journal-voucher-open-references", {
    params: {
      chart_of_account_id: accountId,
      exclude_voucher: excludeVoucherId || undefined,
    },
  }).then((res) => res.data || []);

// A line keeps one or the other: typing a new reference drops the existing
// pick, and picking an existing one drops the typed reference.
export const setLineReference = (line, name, value) => {
  const next = { ...line, [name]: value };
  if (name === "reference" && String(value).trim() !== "") {
    next.existing_reference = "";
  }
  if (name === "existing_reference" && value) {
    next.reference = "";
  }
  return next;
};

const sideLabel = (side) => (side === "dr" ? "Dr" : "Cr");

const referenceOptionLabel = (option, { context }) =>
  context === "menu" && option.serial_number ? (
    <div>
      <div>{option.label}</div>
      <small className="text-muted">
        {option.serial_number} &middot; {money(option.balance)}{" "}
        {sideLabel(option.side)} due of {money(option.opening)}
      </small>
    </div>
  ) : (
    option.label
  );

/**
 * The Reference and Existing Reference cells of a journal line. References are
 * tracked per chart of account, so both wait for the line's account, and
 * filling one disables the other.
 */
export const ReferenceCells = ({ line, options, onChange }) => {
  const hasAccount = line.chart_of_account_id > 0;
  const reference = line.reference || "";
  const existing = line.existing_reference || "";
  const selected = existing
    ? Array.from(options || []).find(
        (option) =>
          String(option.value).toLowerCase() === existing.toLowerCase(),
      ) || { value: existing, label: existing }
    : null;

  return (
    <>
      <td>
        <input
          type="text"
          className="form-control"
          name="reference"
          maxLength={100}
          placeholder={hasAccount ? "New reference" : "Select account first"}
          title={
            existing ? "Clear Existing Reference to enter a new one" : ""
          }
          value={reference}
          disabled={!hasAccount || existing !== ""}
          onChange={(e) => onChange("reference", e.target.value)}
        />
      </td>
      <td>
        <Select
          className="basic-single"
          classNamePrefix="select"
          name="existing_reference"
          placeholder={hasAccount ? "Select reference" : "Select account first"}
          options={options || []}
          value={selected}
          formatOptionLabel={referenceOptionLabel}
          onChange={(option) =>
            onChange("existing_reference", option ? option.value : "")
          }
          noOptionsMessage={() => "No open reference on this account"}
          isDisabled={!hasAccount || reference.trim() !== ""}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
          }}
          maxMenuHeight={320}
          isClearable
          isSearchable
        />
        {selected && selected.balance !== undefined && (
          <small className="jv-head-note">
            {money(selected.balance)} {sideLabel(selected.side)} outstanding
          </small>
        )}
      </td>
    </>
  );
};

// Running difference shown under the lines while the voucher is being built.
export const BalanceIndicator = ({ debit, credit }) => {
  const difference = paisa(debit) - paisa(credit);
  const empty = !paisa(debit) && !paisa(credit);

  if (empty) {
    return (
      <span className="jv-balance is-empty">Enter debit and credit lines</span>
    );
  }
  if (difference === 0) {
    return (
      <span className="jv-balance is-ok">
        <i className="fa fa-check-circle me-1"></i>Balanced
      </span>
    );
  }
  return (
    <span className="jv-balance is-off">
      <i className="fa fa-exclamation-circle me-1"></i>
      Out by {money(Math.abs(difference) / 100)}{" "}
      {difference > 0 ? "(more debit)" : "(more credit)"}
    </span>
  );
};

// One look for every Journal Voucher screen. The other Accounts modules were
// styled after these pages.
export const JournalVoucherStyles = () => (
  <style>
    {`
      /* ---- Panels & form ---- */
      .jv-panel, .jv-form-panel {
        background: #fff;
        border: 1px solid #e6e8f0;
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
        margin-bottom: 20px;
      }
      .jv-panel-head, .jv-form-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 14px 20px;
        border-bottom: 1px solid #e6e8f0;
        font-size: 0.95rem;
        font-weight: 600;
        color: ${NAVY};
      }
      .jv-head-note {
        font-size: 0.75rem;
        font-weight: 500;
        color: #8b90a5;
      }
      .jv-panel-body, .jv-form-body { padding: 20px; }
      .jv-divider {
        border-top: 1px dashed #e6e8f0;
        margin: 18px 0 16px;
      }
      .jv-label,
      .jv-form-panel label {
        display: block;
        margin-bottom: 6px;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #6b7280;
      }
      .jv-panel .form-control:focus,
      .jv-form-panel .form-control:focus {
        border-color: ${NAVY};
        box-shadow: 0 0 0 0.2rem rgba(42, 41, 98, 0.12);
      }
      .jv-form-panel .required { color: ${RED}; font-size: 0.5rem; vertical-align: super; }
      .jv-form-panel .form-control[readonly] {
        background: #f7f8fc;
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        font-weight: 600;
        color: ${NAVY};
      }
      .jv-serial {
        display: inline-block;
        padding: 3px 9px;
        border-radius: 4px;
        border: 1px solid #e6e8f0;
        background: #f7f8fc;
        color: ${NAVY};
        font-size: 0.78rem;
        font-weight: 600;
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        vertical-align: middle;
      }

      /* ---- Journal line editor ---- */
      .jv-line-table thead th {
        padding: 10px 8px;
        background: ${NAVY};
        color: #fff;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        border: 0;
        white-space: nowrap;
      }
      .jv-line-table tbody td {
        padding: 8px;
        border-bottom: 1px solid #f0f1f6;
        vertical-align: top;
      }
      .jv-line-table tbody tr:hover { background: #fafbfd; }
      .jv-line-table .form-control,
      .jv-line-table .select__control { font-size: 0.86rem; }
      .jv-line-table tfoot th {
        padding: 11px 8px;
        background: #f2f3f9;
        border-top: 2px solid ${NAVY};
        font-size: 0.92rem;
        color: ${NAVY};
      }
      .jv-line-table .jv-line-no {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        margin-top: 7px;
        border-radius: 50%;
        background: #f2f3f9;
        color: #6b7280;
        font-size: 0.72rem;
        font-weight: 600;
      }
      .jv-balance {
        font-size: 0.82rem;
        font-weight: 600;
        white-space: nowrap;
      }
      .jv-balance.is-ok { color: ${GREEN}; }
      .jv-balance.is-off { color: ${RED}; }
      .jv-balance.is-empty { color: #8b90a5; font-weight: 500; }

      .jv-num {
        text-align: right;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
      }

      /* ---- Stat strip & chips ---- */
      .jv-stat {
        height: 100%;
        padding: 16px 18px;
        background: #fff;
        border: 1px solid #e6e8f0;
        border-left: 4px solid ${NAVY};
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
      }
      .jv-stat-value {
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1.2;
        color: ${NAVY};
        font-variant-numeric: tabular-nums;
      }
      .jv-chip {
        padding: 0.25rem 0.85rem;
        border: 1px solid #d6d9e6;
        border-radius: 20px;
        background: #fff;
        color: #4b5563;
        font-size: 0.78rem;
        font-weight: 600;
        transition: all 0.15s ease;
      }
      .jv-chip:hover { border-color: ${NAVY}; color: ${NAVY}; }
      .jv-chip.active {
        background: ${NAVY};
        border-color: ${NAVY};
        color: #fff;
      }
      .jv-active-filters {
        font-size: 0.82rem;
        color: ${NAVY};
      }

      /* ---- Postings grid (list page) ---- */
      .jv-table .p-datatable-thead > tr > th {
        background: ${NAVY};
        color: #fff;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 11px 12px;
        border: 0;
        white-space: nowrap;
      }
      .jv-table .p-datatable-thead > tr > th .p-sortable-column-icon,
      .jv-table .p-datatable-thead > tr > th:hover {
        color: #fff;
        background: ${NAVY};
      }
      .jv-table .p-datatable-tbody > tr > td {
        padding: 8px 12px;
        font-size: 0.85rem;
        color: #313747;
        border-left: 0;
        border-right: 0;
        border-bottom: 1px solid #f0f1f6;
        vertical-align: middle;
      }
      /* Striping would cut across the voucher blocks, so the grouping is
         carried by a rule above each voucher instead. */
      .jv-table .p-datatable-tbody > tr,
      .jv-table .p-datatable-tbody > tr:nth-child(even) {
        background: #fff;
      }
      /* The voucher's head row: its own totals, then its lines beneath. */
      .jv-table .p-datatable-tbody > tr.jv-row-head > td {
        background: #f2f3f9;
        border-top: 2px solid ${NAVY};
        font-weight: 700;
        color: ${NAVY};
      }
      .jv-table .p-datatable-tbody > tr.jv-row-head:hover > td {
        background: #eceef7;
      }
      .jv-table .p-datatable-tbody > tr.jv-row-line:hover > td {
        background: #f6f7fc;
      }
      /* On a head row this column carries the narration, not an account. */
      .jv-table tr.jv-row-head .jv-account {
        font-weight: 400;
        font-style: italic;
        color: #5b6172;
      }
      .jv-table tr.jv-row-line > td:first-child { padding-left: 26px; }
      .jv-table .jv-num { white-space: nowrap; }
      .jv-table .jv-num .p-column-header-content {
        justify-content: flex-end;
      }
      .jv-table .jv-nil { color: #c7cad6; }
      .jv-badge {
        display: inline-block;
        padding: 2px 9px;
        border-radius: 20px;
        font-size: 0.7rem;
        font-weight: 600;
        letter-spacing: 0.02em;
      }
      .jv-badge-journal { background: #e8e8f4; color: ${NAVY}; }
      .jv-badge-payment { background: #fdeaea; color: ${RED}; }
      .jv-badge-receipt { background: #e4f2e8; color: ${GREEN}; }
      .jv-badge-contra  { background: #fdf0dd; color: #98620c; }
      .jv-table .p-datatable-footer,
      .jv-table .p-paginator {
        background: #fafbfd;
        border: 0;
        border-top: 1px solid #e6e8f0;
        font-size: 0.82rem;
        color: #5b6172;
      }
      .jv-table .p-paginator .p-highlight {
        background: ${NAVY};
        border-color: ${NAVY};
        color: #fff;
      }
      .jv-table .reactDataTable { padding-bottom: 12px; }

      .jv-empty {
        padding: 48px 20px;
        text-align: center;
        color: #8b90a5;
      }
      .jv-empty i { font-size: 2rem; color: #d7dae8; display: block; margin-bottom: 10px; }
    `}
  </style>
);
